/**
 * Mention Parser - Parse Functions
 *
 * High-level functions for parsing mentions and resolving to user IDs.
 */

import {
  STRUCTURED_MENTION_REGEX,
  LEGACY_MENTION_REGEX,
} from "./regex.js";
import { extractStructuredMentions } from "./extract.js";
import { resolveMentionsToUserIds } from "./resolve.js";

/**
 * Parse text, extract mentions, and resolve to user IDs in one call.
 *
 * Strategy (fast path first):
 * 1. Extract structured tokens (@[Name](userId)) — userId is embedded, no lookup
 * 2. Fall back to legacy @username — requires Firestore lookup
 *
 * @param {string} text - Text containing @mentions
 * @returns {Promise<string[]>} Array of user IDs for mentioned users
 */
export async function parseMentions(text) {
  if (!text || typeof text !== "string") {
    return [];
  }

  // ── Fast path: structured tokens embed userId directly ──
  const structuredMentions = extractStructuredMentions(text);
  const structuredUserIds = structuredMentions.map((m) => m.userId);

  // ── Legacy path: extract @username and resolve via Firestore ──
  // Strip structured tokens first so legacy regex doesn't match fragments
  const textWithoutStructured = text.replace(STRUCTURED_MENTION_REGEX, "");
  const legacyMatches = textWithoutStructured.match(LEGACY_MENTION_REGEX);

  if (legacyMatches) {
    const legacyUsernames = [
      ...new Set(legacyMatches.map((m) => m.substring(1).toLowerCase())),
    ];
    const resolved = await resolveMentionsToUserIds(legacyUsernames);
    const legacyUserIds = resolved.map((r) => r.userId);

    // Merge: structured first, then legacy
    return [...structuredUserIds, ...legacyUserIds];
  }

  return structuredUserIds;
}

/**
 * Parse text and return full mention data (displayName/username + userId pairs).
 * Handles both structured tokens (no lookup) and legacy (requires lookup).
 *
 * @param {string} text - Text containing @mentions
 * @returns {Promise<Array<{username: string, userId: string}>>}
 */
export async function parseMentionsWithData(text) {
  if (!text || typeof text !== "string") {
    return [];
  }

  const results = [];
  const seenUserIds = new Set();

  // ── Fast path: structured tokens ──
  const structuredMentions = extractStructuredMentions(text);
  for (const { displayName, userId } of structuredMentions) {
    if (!seenUserIds.has(userId)) {
      results.push({ username: displayName, userId });
      seenUserIds.add(userId);
    }
  }

  // ── Legacy path: @username requires Firestore lookup ──
  const textWithoutStructured = text.replace(STRUCTURED_MENTION_REGEX, "");
  const legacyMatches = textWithoutStructured.match(LEGACY_MENTION_REGEX);

  if (legacyMatches) {
    const legacyUsernames = [
      ...new Set(legacyMatches.map((m) => m.substring(1).toLowerCase())),
    ];
    const resolved = await resolveMentionsToUserIds(legacyUsernames);
    for (const { username, userId } of resolved) {
      if (!seenUserIds.has(userId)) {
        results.push({ username, userId });
        seenUserIds.add(userId);
      }
    }
  }

  return results;
}
